import { EditorPanelItem } from '@editor/interfaces/editor'
import { InspectionItem } from '@editor/interfaces/inspection'
import { InspectionGroup } from '@editor/interfaces/inspectionGroup'
import { Cylinder, Polygon } from '@editor/interfaces/shape'

import { ERROR_PROCESS, getCommonErrorMessage, getErrorText, isErrorBody, processErrorHandler } from './ErrorHandler'
import { GET_INSPECTION_AREA_API_URL } from './InspectionArea'
import { isInspectionItem } from './InspectionSheet'
import { GET_RPOJECTS_API_URL } from './Projects'
import { deriveShapeType, isShape } from './Shape'
import { GenericError } from './errors/GenericError'

export const DEFAULT_INSPECTION_GROUPS = [
  '腐食',
  '亀裂',
  'ゆるみ・脱落',
  '破断',
  '防食機能の劣化',
  'ひび割れ',
  '剥離・鉄筋露出',
  '漏水・遊離石灰',
  '抜け落ち',
  '補修・補強材の損傷',
  '床版ひび割れ',
  '浮き',
  '遊間の異常',
  '定着部の異常',
  '変色・劣化',
  '支承部の機能障害',
  'その他（鉄筋腐食・漏水等）',
  '変形・欠損',
  '漏水・滞水',
  '異常な音・振動',
  '異常なたわみ',
  '土砂詰まり',
  '沈下・移動・傾斜',
  '洗掘',
]

/**
 * Check if the object is an instance of InspectionGroup.
 * @param obj The object to check.
 */
export const isInspectionGroup = (obj: unknown): obj is InspectionGroup => obj instanceof Object && 'group_id' in obj

/**
 * Add a new inspection group.
 * @param accessToken - The access token for authentication.
 * @param projectId - The ID of the project.
 * @param areaId - The ID of the inspection area.
 * @param name - The name of the inspection group.
 * @param color - The color of the inspection group.
 * @param order - The order of the inspection group.
 * @throws GenericError if the request fails.
 */
export const postInspectionGroup = (
  accessToken: string,
  projectId: string,
  areaId: string,
  name: string,
  color: string,
  order: number,
): Promise<InspectionGroup> =>
  fetch(`${GET_INSPECTION_AREA_API_URL(projectId, areaId)}/inspection-groups`, {
    method: 'POST',
    headers: {
      responseType: 'json',
      'X-Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name, color, order }),
  })
    .then(async (res) => {
      if (!res.ok) {
        const body = (await res.json()) as unknown
        if (body instanceof Object && isErrorBody(body)) {
          throw new GenericError(getErrorText(ERROR_PROCESS.INSPECTION_GROUP, body.status_id))
        }

        throw new GenericError(getCommonErrorMessage(ERROR_PROCESS.INSPECTION_GROUP))
      }

      return res.json() as Promise<InspectionGroup>
    })
    .catch((err) => {
      if (err instanceof GenericError) {
        throw err
      }

      throw new GenericError(getCommonErrorMessage(ERROR_PROCESS.INSPECTION_GROUP))
    })

/**
 * Find an inspection group by its ID.
 * @param groups - The list of inspection groups.
 * @returns The inspection group if found, otherwise undefined.
 */
export const findInspectionGroup = (groups: EditorPanelItem[], groupId: string) => {
  const group = groups.find((g) => isInspectionGroup(g.item) && g.item.group_id === groupId)?.item

  if (group && isInspectionGroup(group)) {
    return group
  }

  return undefined
}

/**
 * Get inspection groups within a specific inspection area.
 * @param accessToken - The access token for authentication.
 * @param projectId - The ID of the project.
 * @param areaId - The ID of the inspection area.
 * @param showErrorModal - Function to show error modal.
 */
export const getInspectionGroups = async (
  accessToken: string,
  projectId: string,
  areaId: string | undefined,
  showErrorModal: (message: string) => void,
): Promise<InspectionGroup[] | null> => {
  const doFetch = async (
    allGroups: InspectionGroup[],
    lastEvaluatedKey?: Record<string, string>,
  ): Promise<InspectionGroup[]> => {
    const params = new URLSearchParams({ limit: '1000' })
    if (lastEvaluatedKey) {
      params.append('last_evaluated_key', JSON.stringify(lastEvaluatedKey))
    }

    const baseUrl = areaId ? GET_INSPECTION_AREA_API_URL(projectId, areaId) : GET_RPOJECTS_API_URL(projectId)
    const response = await fetch(`${baseUrl}/inspection-groups?${params.toString()}`, {
      method: 'GET',
      headers: {
        responseType: 'json',
        'X-Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) throw new Error('Failed to get inspection groups')

    const data = (await response.json()) as { results: InspectionGroup[]; last_evaluated_key?: Record<string, string> }
    allGroups.push(...data.results)

    if (data.last_evaluated_key) {
      return doFetch(allGroups, data.last_evaluated_key)
    }

    return allGroups
  }

  try {
    const groups = await doFetch([])
    return groups
  } catch (err) {
    processErrorHandler(err, ERROR_PROCESS.INSPECTION_GROUP, showErrorModal)
    return null
  }
}

/**
 * Update an existing inspection group.
 * @param accessToken - The access token for authentication.
 * @param projectId - The ID of the project.
 * @param areaId - The ID of the inspection area.
 * @param groups - The inspection groups to update.
 * @throws GenericError if the request fails.
 */
export const patchInspectionGroups = (
  accessToken: string,
  projectId: string,
  groups: Partial<Pick<InspectionGroup, 'inspection_area_id' | 'group_id' | 'name' | 'color' | 'order'>>[],
): Promise<InspectionGroup[] | null> =>
  fetch(`${GET_RPOJECTS_API_URL(projectId)}/inspection-groups`, {
    method: 'PATCH',
    headers: {
      responseType: 'json',
      'X-Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(
      groups.map(({ inspection_area_id, group_id, name, color, order }) => ({
        group_id,
        inspection_area_id,
        params: { name, color, order },
      })),
    ),
  })
    .then(async (res) => {
      if (!res.ok) {
        const body = (await res.json()) as unknown
        if (body instanceof Object && isErrorBody(body)) {
          throw new GenericError(getErrorText(ERROR_PROCESS.INSPECTION_GROUP, body.status_id))
        }

        throw new GenericError(getCommonErrorMessage(ERROR_PROCESS.INSPECTION_GROUP))
      }

      return res.json() as Promise<InspectionGroup[]>
    })
    .catch((err) => {
      if (err instanceof GenericError) {
        throw err
      }

      throw new GenericError(getCommonErrorMessage(ERROR_PROCESS.INSPECTION_GROUP))
    })

/**
 * Delete an inspection group.
 * @param accessToken - The access token for authentication.
 * @param projectId - The ID of the project.
 * @param areaId - The ID of the inspection area.
 * @param groupId - The ID of the inspection group to delete.
 * @param showErrorModal - Function to show error modal.
 */
export const deleteInspectionGroup = (
  accessToken: string,
  projectId: string,
  areaId: string,
  groupId: string,
): Promise<boolean> =>
  fetch(`${GET_INSPECTION_AREA_API_URL(projectId, areaId)}/inspection-groups/${encodeURIComponent(groupId)}`, {
    method: 'DELETE',
    headers: {
      responseType: 'json',
      'X-Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
    .then(async (res) => {
      if (!res.ok) {
        const body = (await res.json()) as unknown
        if (body instanceof Object && isErrorBody(body)) {
          throw new GenericError(getErrorText(ERROR_PROCESS.INSPECTION_GROUP, body.status_id))
        }

        throw new GenericError(getCommonErrorMessage(ERROR_PROCESS.INSPECTION_GROUP))
      }

      return true
    })
    .catch((err) => {
      if (err instanceof GenericError) {
        throw err
      }

      throw new GenericError(getCommonErrorMessage(ERROR_PROCESS.INSPECTION_GROUP))
    })

/**
 * Update the order of elements within a specific inspection group.
 *
 * @param access_token - The access token for authentication.
 * @param projectId - The ID of the project.
 * @param items - The elements to update. Must be extended with InspectionGroupable.
 * @returns A promise that resolves to the updated elements.
 */
export const patchElementOrders = (
  access_token: string,
  projectId: string,
  inspectionAreaId: string,
  items: Partial<InspectionItem | Cylinder | Polygon>[],
) => {
  const getElementType = (item: Partial<InspectionItem | Cylinder | Polygon>) => {
    if (isInspectionItem(item)) return 'inspection_items'
    if (isShape(item)) return deriveShapeType(item)

    return null
  }

  const getElementId = (item: Partial<InspectionItem | Cylinder | Polygon>) => {
    if (isInspectionItem(item)) return item.inspection_item_id!
    if (isShape(item)) return item.shape_id
    return null
  }

  return fetch(`${GET_RPOJECTS_API_URL(projectId)}/inspection-group-elements`, {
    method: 'PATCH',
    headers: { 'X-Authorization': `Bearer ${access_token}` },
    body: JSON.stringify(
      items.reduce(
        (acc, item) => {
          const elementType = getElementType(item)
          const elementId = getElementId(item)
          if (elementType && elementId && item.partition_key) {
            acc.push({
              element_type: elementType,
              element_id: elementId,
              element_partition_key: item.partition_key,
              inspection_area_id: inspectionAreaId,
              params: {
                inspection_group_id: item.inspection_group_id || null,
                order_in_inspection_group: item.order_in_inspection_group || null,
              },
            })
          }
          return acc
        },
        [] as {
          element_type: 'inspection_items' | 'polygons' | 'cylinders'
          element_id: string
          element_partition_key: string
          inspection_area_id: string
          params: {
            inspection_group_id: string | null
            order_in_inspection_group: number | null
          }
        }[],
      ),
    ),
  })
    .then(async (res) => {
      if (!res.ok) {
        const body = (await res.json()) as unknown
        if (body instanceof Object && isErrorBody(body)) {
          throw new GenericError(getErrorText(ERROR_PROCESS.INSPECTION_GROUP, body.status_id))
        }

        throw new GenericError(getCommonErrorMessage(ERROR_PROCESS.INSPECTION_GROUP))
      }

      return true
    })
    .catch((err) => {
      if (err instanceof GenericError) {
        throw err
      }

      throw new GenericError(getCommonErrorMessage(ERROR_PROCESS.INSPECTION_GROUP))
    })
}

/**
 * Batch delete inspection groups.
 *
 * @param accessToken - The access token for authentication.
 * @param projectId - The ID of the project.
 * @param ids - An array of objects containing inspection_area_id and group_id.
 */
export const batchDeleteInspectionGroups = (
  accessToken: string,
  projectId: string,
  ids: {
    inspection_area_id: string
    group_id: string
  }[],
): Promise<boolean> =>
  fetch(`${GET_RPOJECTS_API_URL(projectId)}/inspection-groups:batchDelete`, {
    method: 'POST',
    headers: {
      responseType: 'json',
      'X-Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(ids),
  })
    .then(async (res) => {
      if (!res.ok) {
        const body = (await res.json()) as unknown
        if (body instanceof Object && isErrorBody(body)) {
          throw new GenericError(getErrorText(ERROR_PROCESS.INSPECTION_GROUP, body.status_id))
        }

        throw new GenericError(getCommonErrorMessage(ERROR_PROCESS.INSPECTION_GROUP))
      }

      return true
    })
    .catch((err) => {
      if (err instanceof GenericError) {
        throw err
      }

      throw new GenericError(getCommonErrorMessage(ERROR_PROCESS.INSPECTION_GROUP))
    })

/**
 * Validate the group name.
 * - Must not be empty
 * - Must be unique within the current inspection area
 *
 * @param name The name to validate
 * @param itself The current group's ID to exclude from uniqueness check
 * @returns A string error message if validation fails, or true if validation passes
 */
export const validateName = (name: string, targetId: string, groups: EditorPanelItem[]): string | boolean => {
  // Check if the name is empty
  if (!name.trim()) {
    return 'グループ名を入力してください'
  }

  // Check if the name is already used
  const existingGroup = groups.find(
    (group) => group.name === name && isInspectionGroup(group.item) && group.item.group_id !== targetId,
  )
  if (existingGroup) {
    return 'このグループ名はすでに使用されています'
  }

  return true
}

/**
 * Convert a list of EditorPanelItem to InspectionGroup.
 *
 * @param items The list of EditorPanelItem to convert to InspectionGroup.
 */
export const toInspectionGroups = (items: EditorPanelItem[]): InspectionGroup[] =>
  items.filter((it) => isInspectionGroup(it.item)).map((it) => it.item as InspectionGroup)
